import { AxiosError } from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  usePostBaseManuscriptsFlistSearch,
  usePostBaseManuscriptsSearch,
} from '../../../../api/base-manuscripts/base-manuscripts';
import { useGetJudgmentDivisions } from '../../../../api/masters/masters';
import { BaseManuscriptSearchConditions, ErrorInfo } from '../../../../api/model';
import { useGetSectionFirstId } from '../../../../api/sections/sections';
import { useDefaultOptions } from '../../../../hooks/api';
import * as sessionStorageUtils from '../../../../utils/sessionStorage';
import ErrorComponent from '../../../atoms/ErrorComponent';
import NoDataComponent from '../../../atoms/NoDataComponent';
import WaitComponent from '../../../atoms/WaitComponent';
import { BaseFlist } from './BaseFlist';
import { BaseManual } from './BaseManual';
import { BaseManualSearch } from './BaseManualSearch';

/**
 * タブタイトル
 * @returns
 */
function Title() {
  // 言語情報
  const { t } = useTranslation();
  return (
    <Helmet>
      <title>{t('CMS-POC|ベース原稿')}</title>
    </Helmet>
  );
}
function BaseManualPage() {
  // 検索条件部分が開いているかどうか
  const [openSearchConditions, setOpenSearchConditions] = useState(true);
  const { productionId: productionIdString } = useParams();
  // 表示対象の制作管理 ID
  const productionId = useMemo(
    () => (productionIdString != null ? parseInt(productionIdString, 10) : undefined),
    [productionIdString]
  );
  const {
    t,
    i18n: { language },
  } = useTranslation();

  /* ナビゲーション */
  const navigate = useNavigate();
  const location = useLocation();
  const checkPath = (path: string) => location.pathname.indexOf(path) > 0;
  const options = useDefaultOptions(language);

  /* <<<<<<<<<< 判定マスタ取得 */
  const {
    data: Judgmentdata,
    isLoading: judgmentIsLoading,
    isError: judgmentIsError,
    error: judgmentError,
  } = useGetJudgmentDivisions(options);
  /* >>>>>>>>>> */

  // 初期検索条件
  const [defaultConditions] = useState<BaseManuscriptSearchConditions>({ production_id: productionId });
  // 検索条件
  const refConditions = useRef(
    sessionStorageUtils.get<BaseManuscriptSearchConditions>('BaseManuscript.SearchConditions', {
      production_id: productionId,
    })
  );
  /* <<<<<<<<<< 原稿の最初のセクションID取得 */
  const firstIdResponse = useGetSectionFirstId({ production_id: Number(productionId) }, options);
  const [sectionID, setFirstSectionId] = useState<number | undefined>(undefined);
  useEffect(() => {
    if (firstIdResponse != null) {
      setFirstSectionId(firstIdResponse.data?.data.section_id);
    }
  }, [firstIdResponse, setFirstSectionId]);
  /* >>>>>>>>>> */

  /* <<<<<<<<<< ベース原稿テーブル検索 */
  // API
  const {
    data,
    isLoading,
    isError,
    error,
    mutateAsync: searchAsync,
  } = usePostBaseManuscriptsSearch<AxiosError<ErrorInfo>, unknown>(options);
  // 一覧の初回表示
  useEffect(() => {
    const postBaseManusripts = async () => {
      await searchAsync({ data: defaultConditions });
    };
    void postBaseManusripts();
  }, [defaultConditions, searchAsync]);
  /* >>>>>>>>>> */

  /* <<<<<<<<<< F-Listテーブル検索 */
  // API
  const {
    data: flistData,
    isLoading: flistIsLoading,
    isError: flistIsError,
    error: flistError,
    mutateAsync: flistSearchAsync,
  } = usePostBaseManuscriptsFlistSearch<AxiosError<ErrorInfo>, unknown>(options);
  // 一覧の初回表示
  useEffect(() => {
    const postBaseFlist = async () => {
      await flistSearchAsync({ data: defaultConditions });
    };
    void postBaseFlist();
  }, [defaultConditions, flistSearchAsync]);
  /* >>>>>>>>>> */

  // 条件を保存し、検索する
  const setConditionsAndSearch = useCallback(
    (next: BaseManuscriptSearchConditions) => {
      const postBaseManusripts = async () => {
        // API アクセスが成功した場合
        const onSuccess = () => {
          /* sessionStorageUtils.set('BaseManuscript.SearchConditions', next); */
          refConditions.current = next;
        };
        // API アクセスの実行
        await searchAsync({ data: next }, { onSuccess });
      };
      const postBaseFlist = async () => {
        // API アクセスが成功した場合
        const onSuccess = () => {
          /* sessionStorageUtils.set('BaseManuscript.SearchConditions', next); */
          refConditions.current = next;
        };
        // API アクセスの実行
        await flistSearchAsync({ data: next }, { onSuccess });
      };

      void postBaseManusripts();
      void postBaseFlist();
    },
    [searchAsync, flistSearchAsync]
  );

  // 待ち状態
  if (judgmentIsLoading) {
    return (
      <div id="main">
        <WaitComponent />
      </div>
    );
  }

  // エラー
  if (judgmentIsError) {
    return <div id="main">{judgmentIsError && <ErrorComponent error={judgmentError} />}</div>;
  }

  // データが取得できない場合
  if (Judgmentdata.data.judgment_divisions == null) {
    return (
      <div id="main">
        <NoDataComponent />
      </div>
    );
  }

  // 制作管理 ID が設定されていない場合
  if (productionId == null) {
    return <div id="main">{t('この URL は存在しません。')}</div>;
  }

  return (
    <div id="main">
      <Title />
      <section>
        <h3 className="trig">
          <span>
            {t('詳細検索')}
            {/* 検索条件の開閉 */}
            <button
              type="button"
              className="text-button"
              onClick={(e) => {
                e.preventDefault();
                setOpenSearchConditions((pre) => !pre);
              }}
            >
              <i className={`fa-solid ${openSearchConditions ? 'fa-chevron-up' : 'fa-chevron-down'}`} />
            </button>
          </span>
        </h3>
        {/* 検索条件 */}

        <BaseManualSearch
          openSearchConditions={openSearchConditions}
          productionId={productionId}
          defaultConditions={defaultConditions}
          judgmentDivisions={Judgmentdata.data.judgment_divisions}
          conditionsRef={refConditions}
          setConditionsAndSearch={setConditionsAndSearch}
        />

        <div className="product-infofr">
          <div className="info-tab-area">
            <input id="info-tab-btn1" className="info-tab-btn" name="info-tab" type="radio" checked />
            <input id="info-tab-btn2" className="info-tab-btn" name="info-tab" type="radio" />

            <div className="pagetabmenufr">
              <ul className="info-tab-list-wrap">
                <li>
                  <label
                    id="info-tab-list1"
                    className={`info-tab-list ${checkPath('flist') ? 'current' : ''}`}
                    htmlFor="info-tab-btn1"
                    onClick={() => navigate('flist')}
                  >
                    {t('F-List判定結果')}
                  </label>
                </li>
                <li>
                  <label
                    id="info-tab-list2"
                    className={`info-tab-list ${checkPath('flist') ? '' : 'current'}`}
                    htmlFor="info-tab-btn2"
                    onClick={() => navigate('base')}
                  >
                    {t('ベース原稿')}
                  </label>
                </li>
              </ul>
            </div>
            <div className="info-tab-content-wrap wfr">
              <div id="info-tab-content1" className="info-tab-content infolistfr">
                <Routes>
                  <Route
                    path="flist/*"
                    element={
                      <BaseFlist
                        productionId={productionId}
                        sectionId={sectionID}
                        data={flistData}
                        isLoading={flistIsLoading}
                        isError={flistIsError}
                        dataError={flistError}
                      />
                    }
                  />
                  <Route
                    path="base/*"
                    element={
                      <BaseManual
                        productionId={productionId}
                        sectionId={sectionID}
                        data={data}
                        isLoading={isLoading}
                        isError={isError}
                        dataError={error}
                        setConditionsAndSearch={setConditionsAndSearch}
                      />
                    }
                  />
                </Routes>
              </div>
              <div id="info-tab-content2" className="info-tab-content infolistfr">
                <Routes>
                  <Route
                    path="flist/*"
                    element={
                      <BaseFlist
                        productionId={productionId}
                        sectionId={sectionID}
                        data={flistData}
                        isLoading={flistIsLoading}
                        isError={flistIsError}
                        dataError={flistError}
                      />
                    }
                  />
                  <Route
                    path="base/*"
                    element={
                      <BaseManual
                        productionId={productionId}
                        sectionId={sectionID}
                        data={data}
                        isLoading={isLoading}
                        isError={isError}
                        dataError={error}
                        setConditionsAndSearch={setConditionsAndSearch}
                      />
                    }
                  />
                </Routes>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default BaseManualPage;
